import React from "react";
import { Link } from "react-router-dom";
import { TrendingUp, Wallet, Eye, Award, ArrowRight } from "lucide-react";
import { Panel } from "../components/ui";
import { HeroPanel, Step, ProgressBar } from "../components/ui-extras";
import TierBadge from "../components/TierBadge";
import Disclaimer from "../components/Disclaimer";
import useSeo from "../lib/useSeo";
import usePricing from "../lib/pricing";
import { useAuth } from "../context/AuthContext";
import { money, num } from "../lib/format";
import { useTranslation } from "react-i18next";

const TIERS = ["bronze", "silver", "gold", "platinum"];
const SIGNALS = ["followers", "posts", "commentary", "engagement", "positions", "tenure"];

/**
 * The investor handbook: what moves your ranking, what moves your visibility, and
 * how you get paid. Written as one page rather than scattered tooltips, because the
 * questions ("why am I ranked below them?", "when do I reach Gold?") are asked
 * together and answered together.
 */
export default function InvestorGuide() {
  const { t } = useTranslation();
  // The handbook is where an investor works out whether this is worth their time, so
  // every number in it is the live one. The previous version had the ladder and the
  // earnings example written into the copy, and both were a price change out of date.
  // Earnings only exist while payments do (backend/features.py); without them the
  // money panel is not shown and nothing is fetched.
  const { features } = useAuth();
  const pricing = usePricing({ enabled: features.payments });
  const ccy = pricing?.currency || "EUR";
  const pro = pricing?.plans?.pro;
  const reqs = pricing?.rank_requirements;
  const shares = pricing?.share_pct;

  const tierReq = (tier) => {
    if (!reqs) return "";
    if (tier === "bronze") return t("guide.tier.bronze.req");
    const r = reqs[tier];
    return t(r.reviewed ? "guide.reqReviewed" : "guide.req", {
      referrals: num(r.referrals, 0),
      points: num(r.points, 0),
      months: r.months,
    });
  };

  useSeo({ title: t("guide.title"), description: t("guide.seoDesc") });

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <HeroPanel eyebrow={t("guide.eyebrow")} title={t("guide.title")} subtitle={t("guide.subtitle")}
                 testid="guide-hero" />

      <Panel className="card p-5 sm:p-6" testid="guide-ranking">
        <div className="flex items-center gap-2 mb-1">
          <TrendingUp size={17} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("guide.rankingTitle")}</h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{t("guide.rankingBody")}</p>
        <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600 leading-relaxed">
          {t("guide.rankingNote")}
        </div>
      </Panel>

      <Panel className="card p-5 sm:p-6" testid="guide-points">
        <div className="flex items-center gap-2 mb-1">
          <Award size={17} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("guide.pointsTitle")}</h2>
        </div>
        <p className="text-sm text-slate-600">{t("guide.pointsBody")}</p>
        <div className="mt-4 space-y-2.5">
          {SIGNALS.map((k) => (
            <div key={k} className="flex items-start justify-between gap-4 py-2 border-b border-slate-50 last:border-0">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{t(`guide.signal.${k}.name`)}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t(`guide.signal.${k}.why`)}</div>
              </div>
              <span className="text-xs font-semibold text-brand-600 shrink-0 tabular">
                {t(`guide.signal.${k}.weight`)}
              </span>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-4 text-sm text-amber-900 leading-relaxed">
          {t("guide.antiSpam")}
        </div>
      </Panel>

      <Panel className="card p-5 sm:p-6" testid="guide-tiers">
        <h2 className="font-heading font-semibold tracking-tight text-lg mb-1">{t("guide.tiersTitle")}</h2>
        <p className="text-sm text-slate-600">{t("guide.tiersBody")}</p>
        <div className="mt-4 space-y-3">
          {TIERS.map((tier, i) => (
            <div key={tier} className="rounded-xl border border-slate-100 p-4" data-testid={`guide-tier-${tier}`}>
              <div className="flex items-center justify-between gap-3">
                <TierBadge tier={tier} />
                <span className="text-xs text-slate-400">{tierReq(tier)}</span>
              </div>
              <ProgressBar pct={(i + 1) * 25} tone={tier === "gold" ? "gold" : "brand"} className="mt-3" />
              <p className="text-sm text-slate-600 mt-2.5">
                {t(`guide.tier.${tier}.what`, { share: shares?.[tier] ?? "—" })}
              </p>
            </div>
          ))}
        </div>
      </Panel>

      <Panel className="card p-5 sm:p-6" testid="guide-visibility">
        <div className="flex items-center gap-2 mb-1">
          <Eye size={17} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("guide.visibilityTitle")}</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">{t("guide.visibilityBody")}</p>
        <Step n={1} title={t("guide.vis1")}>{t("guide.vis1Body")}</Step>
        <Step n={2} title={t("guide.vis2")}>{t("guide.vis2Body")}</Step>
        <Step n={3} title={t("guide.vis3")}>{t("guide.vis3Body")}</Step>
      </Panel>

      {features.payments && (
      <Panel className="card p-5 sm:p-6" testid="guide-earnings">
        <div className="flex items-center gap-2 mb-1">
          <Wallet size={17} className="text-brand-500" />
          <h2 className="font-heading font-semibold tracking-tight text-lg">{t("guide.earningsTitle")}</h2>
        </div>
        <p className="text-sm text-slate-600 leading-relaxed">{t("guide.earningsBody")}</p>
        {/* The worked example, computed rather than written. Anyone reading this will
            check the arithmetic, so the numbers have to be the ones we actually use. */}
        <div className="mt-4 rounded-xl bg-slate-50 p-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line"
             data-testid="guide-earnings-math">
          {pro ? t("guide.earningsMath", {
            price: money(pro.gross_eur, ccy),
            vat: money(pro.vat_eur, ccy),
            vatPct: pricing.vat_pct,
            processing: money(pro.processing_eur, ccy),
            base: money(pro.distributable_base_eur, ccy),
            low: money(pro.per_tier_eur.bronze, ccy),
            high: money(pro.per_tier_eur.platinum, ccy),
          }) : ""}
        </div>
        <p className="text-sm text-slate-600 mt-4 leading-relaxed">{t("guide.earningsHonest")}</p>
        <Link to="/partner" className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 mt-4">
          {t("guide.seeEarnings")} <ArrowRight size={14} />
        </Link>
      </Panel>
      )}

      <Panel className="card p-5 sm:p-6" testid="guide-integrity">
        <h2 className="font-heading font-semibold tracking-tight text-lg mb-1">{t("guide.integrityTitle")}</h2>
        <p className="text-sm text-slate-600 leading-relaxed">{t("guide.integrityBody")}</p>
      </Panel>

      <Disclaimer />
    </div>
  );
}
